import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, Car, Route, DollarSign, TrendingUp, 
  Activity, UserCheck, AlertCircle, LogOut,
  ChevronLeft, Menu, X, Bell, Settings
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { adminService } from '@/services/adminService';
import type { DashboardStats } from '@/types';

const OwnerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const dashboardStats = await adminService.getDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    { icon: Users, label: 'المستخدمين', path: '/owner/users', color: 'bg-blue-500' },
    { icon: Car, label: 'السائقين', path: '/owner/drivers', color: 'bg-green-500' },
    { icon: Route, label: 'الرحلات', path: '/owner/rides', color: 'bg-purple-500' },
    { icon: DollarSign, label: 'المالية', path: '/owner/finance', color: 'bg-yellow-500' },
    { icon: Settings, label: 'الإعدادات', path: '/owner/settings', color: 'bg-gray-500' },
    { icon: AlertCircle, label: 'الدعم', path: '/owner/support', color: 'bg-red-500' },
  ];

  const statCards = [
    { 
      label: 'إجمالي المستخدمين', 
      value: stats?.totalUsers || 0, 
      icon: Users, 
      color: 'bg-blue-500',
      trend: '+12%'
    },
    { 
      label: 'إجمالي السائقين', 
      value: stats?.totalDrivers || 0, 
      icon: Car, 
      color: 'bg-green-500',
      trend: '+8%'
    },
    { 
      label: 'الرحلات اليوم', 
      value: stats?.todayRides || 0, 
      icon: Route, 
      color: 'bg-purple-500',
      trend: '+24%'
    },
    { 
      label: 'الإيرادات اليوم', 
      value: `${(stats?.todayRevenue || 0).toLocaleString()} ج.س`, 
      icon: DollarSign, 
      color: 'bg-yellow-500',
      trend: '+15%'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 right-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl">رحلة</span>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="space-y-2">
            <button className="w-full flex items-center gap-3 px-4 py-3 bg-primary-50 text-primary-600 rounded-xl">
              <Activity className="w-5 h-5" />
              <span className="font-medium">لوحة التحكم</span>
            </button>
            
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-xl transition-colors"
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:mr-64">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4">
              <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-xl">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium">
                    {currentUser?.name?.charAt(0) || 'م'}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <p className="font-medium text-sm">{currentUser?.name || 'المالك'}</p>
                  <p className="text-gray-500 text-xs">مدير النظام</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">لوحة التحكم</h1>
            <p className="text-gray-500">نظرة عامة على أداء التطبيق</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className={`${stat.color} p-3 rounded-xl`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-green-500 text-sm font-medium flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    {stat.trend}
                  </span>
                </div>
                <p className="text-gray-500 text-sm mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
            <h2 className="text-lg font-bold text-gray-800 mb-4">إجراءات سريعة</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {menuItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div className={`${item.color} p-4 rounded-xl`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Verifications */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800">التحققات المعلقة</h3>
                <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-medium">
                  {stats?.pendingVerifications || 0}
                </span>
              </div>
              <p className="text-gray-500 text-sm">
                سائقين في انتظار التحقق من مستنداتهم
              </p>
              <button 
                onClick={() => navigate('/owner/drivers')}
                className="mt-4 text-primary-600 text-sm font-medium flex items-center gap-1"
              >
                عرض الكل
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>

            {/* Open Tickets */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-800">تذاكر الدعم</h3>
                <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium">
                  {stats?.openTickets || 0}
                </span>
              </div>
              <p className="text-gray-500 text-sm">
                تذاكر دعم فني تحتاج إلى الرد
              </p>
              <button 
                onClick={() => navigate('/owner/support')}
                className="mt-4 text-primary-600 text-sm font-medium flex items-center gap-1"
              >
                عرض الكل
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default OwnerDashboard;
